import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import proxy from "../proxy";
import useStockPrices from "../hooks/useStockPrices";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Text,
  Center,
  Spinner,
  VStack,
  HStack,
  useBreakpointValue,
} from "@chakra-ui/react";

// ─── Colors ──────────────────────────────────────────────────
const SECTOR_COLORS = {
  "Real Estate": "#48BB78",
  Healthcare: "#4299E1",
  "Health Care": "#4299E1",
  Technology: "#9F7AEA",
  Financials: "#F6AD55",
  "Communication Services": "#76E4F7",
  "Consumer Staples": "#ECC94B",
  Energy: "#FC8181",
  Utilities: "#FAF089",
  Materials: "#FBB6CE",
  Industrials: "#B794F4",
  Other: "#718096",
};

const getColor = (sector) => SECTOR_COLORS[sector] ?? "#718096";

// ─── Tooltip ─────────────────────────────────────────────────
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: p } = payload[0];
  return (
    <Box
      bg="gray.900"
      border="1px solid"
      borderColor="whiteAlpha.200"
      borderRadius="lg"
      px={3}
      py={2}
      boxShadow="0 8px 20px rgba(0,0,0,0.4)"
    >
      <Text color="white" fontWeight="bold" fontSize="xs">
        {name}
      </Text>
      <Text color="teal.300" fontSize="sm" fontWeight="bold">
        $
        {value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </Text>
      <Text color="gray.400" fontSize="xs">
        {p.percent}% of portfolio
      </Text>
      <Text color="gray.500" fontSize="2xs">
        {p.stockCount} {p.stockCount === 1 ? "stock" : "stocks"}
      </Text>
    </Box>
  );
};

// ─── Legend Item ──────────────────────────────────────────────
const LegendItem = ({ name, percent, value, color, stockCount }) => (
  <HStack
    justify="space-between"
    w="full"
    py={1}
    px={2}
    borderRadius="md"
    transition="all 0.2s"
    _hover={{ bg: "whiteAlpha.100" }}
  >
    <HStack spacing={2} flex={1} minW={0}>
      <Box
        w="10px"
        h="10px"
        borderRadius="sm"
        bg={color}
        flexShrink={0}
        boxShadow={`0 0 6px ${color}66`}
      />
      <Text fontSize="xs" color="gray.300" noOfLines={1}>
        {name}
      </Text>
    </HStack>
    <HStack spacing={3} flexShrink={0}>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">
        {stockCount}x
      </Text>
      <Text fontSize="xs" color="white" fontWeight="bold" minW="40px" textAlign="right">
        {percent}%
      </Text>
    </HStack>
  </HStack>
);

// ─── Main Component ──────────────────────────────────────────
const SectorAllocation = () => {
  const [stocks, setStocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { prices } = useStockPrices();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const chartSize = useBreakpointValue({ base: 140, md: 160 });
  const innerRadius = useBreakpointValue({ base: 40, md: 48 });
  const outerRadius = useBreakpointValue({ base: 62, md: 72 });

  useEffect(() => {
    axios
      .get(`${proxy}server_mts/GetStocks.php`)
      .then((res) => setStocks(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStocks([]))
      .finally(() => setIsLoading(false));
  }, []);

  const { chartData, total } = useMemo(() => {
    const sectorMap = {};
    const sectorCounts = {};

    stocks.forEach((stock) => {
      const sector = stock.sector || "Other";
      // Use current price if available, fallback to avg price
      const currentPrice = prices[stock.ticker] || parseFloat(stock.average_price_share || 0);
      const value = parseFloat(stock.nr_shares || 0) * currentPrice;

      sectorMap[sector] = (sectorMap[sector] || 0) + value;
      sectorCounts[sector] = (sectorCounts[sector] || 0) + 1;
    });

    const total = Object.values(sectorMap).reduce((s, v) => s + v, 0);
    const chartData = Object.entries(sectorMap)
      .map(([name, value]) => ({
        name,
        value: Math.round(value * 100) / 100,
        percent: total > 0 ? ((value / total) * 100).toFixed(1) : "0",
        stockCount: sectorCounts[name] || 0,
      }))
      .sort((a, b) => b.value - a.value);

    return { chartData, total };
  }, [stocks, prices]);

  if (isLoading) {
    return (
      <Center h="120px">
        <Spinner color="teal.400" size="md" />
      </Center>
    );
  }

  if (!chartData.length) return null;

  // Find largest sector for center label
  const topSector = chartData[0];

  return (
    <Box
      maxW="700px"
      mx="auto"
      bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
      borderRadius="xl"
      p={{ base: 3, md: 4 }}
      border="1px solid"
      borderColor="whiteAlpha.100"
      w="100%"
      position="relative"
      overflow="hidden"
      transition="all 0.3s"
      _hover={{
        borderColor: "whiteAlpha.200",
        boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
      }}
    >
      {/* Top accent line */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="2px"
        bgGradient="linear(to-r, green.400, teal.400)"
        opacity={0.5}
      />

      {/* Header */}
      <HStack justify="space-between" align="center" mb={{ base: 2, md: 3 }}>
        <Text
          fontSize={{ base: "xs", md: "sm" }}
          color="teal.300"
          fontWeight="bold"
          letterSpacing="wide"
          textTransform="uppercase"
        >
          📊 Sector Allocation
        </Text>
        <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
          Total:{" "}
          <Text as="span" color="gray.300" fontWeight="semibold">
            $
            {total.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Text>
      </HStack>

      {/* Chart + Legend — always side by side */}
      <HStack spacing={{ base: 3, md: 4 }} align="center">
        {/* Donut chart */}
        <Box
          position="relative"
          h={`${chartSize}px`}
          w={`${chartSize}px`}
          minW={`${chartSize}px`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                paddingAngle={2}
                dataKey="value"
                strokeWidth={0}
                animationBegin={0}
                animationDuration={800}
              >
                {chartData.map((entry) => (
                  <Cell key={entry.name} fill={getColor(entry.name)} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center label */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            textAlign="center"
            pointerEvents="none"
          >
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="black"
              color="white"
              lineHeight="1"
            >
              {chartData.length}
            </Text>
            <Text fontSize="2xs" color="gray.500" lineHeight="1.2">
              sectors
            </Text>
          </Box>
        </Box>

        {/* Legend */}
        <VStack spacing={0} align="stretch" flex={1} w="full">
          {chartData.map((entry) => (
            <LegendItem
              key={entry.name}
              name={entry.name}
              percent={entry.percent}
              value={entry.value}
              color={getColor(entry.name)}
              stockCount={entry.stockCount}
            />
          ))}
        </VStack>
      </HStack>

      {/* Concentration warning — outside the HStack */}
      {topSector && parseFloat(topSector.percent) >= 40 && (
        <Box
          mt={2}
          px={2}
          py={1.5}
          bg="rgba(252, 129, 129, 0.1)"
          borderRadius="md"
          border="1px dashed"
          borderColor="rgba(252, 129, 129, 0.3)"
        >
          <Text fontSize="2xs" color="red.300">
            ⚠️ {topSector.name} is {topSector.percent}% — consider diversifying
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default SectorAllocation;